import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  WorkflowMatchIdentifierListDto,
  WorkflowMatchIdentifierDto,
  CreateWorkflowMatchIdentifierDto,
  UpdateWorkflowMatchIdentifierDto,
  BulkAssignWorkflowMatchIdentifiersDto
} from '../types';
import { WorkflowMatchIdentifierService } from '../services/workflow-match-identifier-service';
import httpClient from '../services/http-client';

const workflowMatchIdentifierService = new WorkflowMatchIdentifierService(httpClient);

/**
 * Hook to get workflow match identifiers
 */
export function useWorkflowMatchIdentifiers(
  workflowId: string,
  params?: { page?: number; pageSize?: number },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ['workflow-match-identifiers', workflowId, params],
    queryFn: () => workflowMatchIdentifierService.getWorkflowMatchIdentifiers(workflowId, params),
    enabled: options?.enabled !== false && !!workflowId,
    staleTime: 0, // Always consider data stale to ensure fresh fetches
    gcTime: 0, // Don't cache the data (gcTime replaces cacheTime in newer versions)
  });
}

/**
 * Hook to create workflow match identifier
 */
export function useCreateWorkflowMatchIdentifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, data }: { 
      workflowId: string; 
      data: CreateWorkflowMatchIdentifierDto 
    }) => workflowMatchIdentifierService.createWorkflowMatchIdentifier(workflowId, data),
    onSuccess: (_, { workflowId }) => {
      // Invalidate and refetch workflow match identifiers (using partial match)
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-match-identifiers', workflowId],
        exact: false // This will invalidate all queries that start with this key
      });
      // Also invalidate workflow data to update match criteria
      queryClient.invalidateQueries({
        queryKey: ['workflow', workflowId]
      });
    },
  });
}

/**
 * Hook to update workflow match identifier
 */
export function useUpdateWorkflowMatchIdentifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      workflowId, 
      id, 
      data 
    }: { 
      workflowId: string; 
      id: string; 
      data: UpdateWorkflowMatchIdentifierDto 
    }) => workflowMatchIdentifierService.updateWorkflowMatchIdentifier(
      workflowId, 
      id, 
      data
    ),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-match-identifiers', workflowId] 
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow', workflowId]
      });
    },
  });
}

/**
 * Hook to delete workflow match identifier
 */
export function useDeleteWorkflowMatchIdentifier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      workflowId, 
      id 
    }: { 
      workflowId: string; 
      id: string; 
    }) => workflowMatchIdentifierService.deleteWorkflowMatchIdentifier(
      workflowId, 
      id
    ),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-match-identifiers', workflowId],
        exact: false 
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow', workflowId]
      });
    },
  });
}

/**
 * Hook to bulk assign match identifiers
 */
export function useBulkAssignWorkflowMatchIdentifiers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      workflowId, 
      data 
    }: { 
      workflowId: string; 
      data: BulkAssignWorkflowMatchIdentifiersDto 
    }) => workflowMatchIdentifierService.bulkAssignMatchIdentifiers(workflowId, data),
    onSuccess: (_, { workflowId }) => {
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-match-identifiers', workflowId],
        exact: false 
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow', workflowId]
      });
    },
  });
}

/**
 * Hook to clear all workflow match identifiers
 */
export function useClearWorkflowMatchIdentifiers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowId: string) => 
      workflowMatchIdentifierService.clearWorkflowMatchIdentifiers(workflowId),
    onSuccess: (_, workflowId) => {
      queryClient.invalidateQueries({ 
        queryKey: ['workflow-match-identifiers', workflowId],
        exact: false 
      });
      queryClient.invalidateQueries({
        queryKey: ['workflow', workflowId]
      });
    },
  });
}
